import  {  useState } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Radio,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useGetQuizesQuery } from "../../Apis/quizApi";
import { useCreateQuestionMutation } from "../../Apis/questionApi";
import { withAdminAuth } from "../../HOC";
import { Add, Remove, ArrowBack } from "@mui/icons-material";

interface Answer {
  answerName: string;
  isCorrect: boolean;
}

interface Question {
  questionName: string;
  answers: Answer[];
}



function CreateQuestion() {
  const [selectedQuiz, setSelectedQuiz] = useState<number>(0);
  const [questions, setQuestions] = useState<Question[]>([
    { questionName: "", answers: [{ answerName: "", isCorrect: false }] },
  ]);
  const navigate = useNavigate();
  const { data, isLoading } = useGetQuizesQuery(null);
  const [createQuestion, { isLoading: creating }] = useCreateQuestionMutation();

  const addQuestion = () => {
    setQuestions([
      ...questions,
      { questionName: "", answers: [{ answerName: "", isCorrect: false }] },
    ]);
  };

  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const addAnswer = (qIndex: number) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].answers.push({ answerName: "", isCorrect: false });
    setQuestions(newQuestions);
  };

  const removeAnswer = (qIndex: number, aIndex: number) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].answers.splice(aIndex, 1);
    setQuestions(newQuestions);
  };

  const handleQuestionChange = (index: number, value: string) => {
    const newQuestions = [...questions];
    newQuestions[index].questionName = value;
    setQuestions(newQuestions);
  };

  const handleAnswerChange = (
    qIndex: number,
    aIndex: number,
    value: string
  ) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].answers[aIndex].answerName = value;
    setQuestions(newQuestions);
  };

  const handleCorrectAnswer = (qIndex: number, aIndex: number) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].answers = newQuestions[qIndex].answers.map(
      (a, i) => ({
        ...a,
        isCorrect: i === aIndex,
      })
    );
    setQuestions(newQuestions);
  };

  const handleSubmit = async () => {
    if (selectedQuiz === 0) {
      alert("Please select a quiz");
      return;
    }

    for (const q of questions) {
      if (!q.answers.some((a) => a.isCorrect)) {
        alert(
          `Each question must have at least one correct answer. Check question: ${q.questionName}`
        );
        return;
      }
    }

    const payload = questions.map((q) => ({
      questionName: q.questionName,
      quizId: selectedQuiz,
      answers: q.answers.map((a) => ({
        answerName: a.answerName,
        isCorrect: a.isCorrect,
      })),
    }));

    try {
      await createQuestion(payload).unwrap();
      alert("Questions created successfully!");
      navigate("/Question/List");
    } catch (error) {
      console.error("Failed to create questions:", error);
      alert("Failed to create questions");
    }
  };

  return (
    <Box sx={{ maxWidth: "1000px", mx: "auto", mt: 4 }}>
      <Card>
        <CardHeader
          title={
            <Typography variant="h5" sx={{ color: "#eb3c96", fontWeight: "bold" }}>
              Create Question
            </Typography>
          }
        />
        <CardContent>
          {/* Quiz Select */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Select Quiz</InputLabel>
            <Select
              value={selectedQuiz}
              label="Select Quiz"
              onChange={(e) => setSelectedQuiz(Number(e.target.value))}
              disabled={isLoading}
            >
              <MenuItem value={0}>-- Select a Quiz --</MenuItem>
              {data?.result
                ?.filter((quiz: any) => quiz.isActive)
                .map((quiz: any) => (
                  <MenuItem key={quiz.id} value={quiz.id}>
                    {quiz.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {questions.map((q, qIndex) => (
            <Card key={qIndex} sx={{ mb: 3, boxShadow: 3 }}>
              <CardContent>
                <TextField
                  fullWidth
                  label={`Question ${qIndex + 1}`}
                  value={q.questionName}
                  onChange={(e) =>
                    handleQuestionChange(qIndex, e.target.value)
                  }
                  sx={{ mb: 2 }}
                />

                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  size="small"
                  onClick={() => addAnswer(qIndex)}
                  sx={{ mb: 2 }}
                >
                  Add Answer
                </Button>

                {q.answers.map((a, aIndex) => (
                  <Grid
                    container
                    spacing={1}
                    alignItems="center"
                    key={aIndex}
                    sx={{ mb: 1 }}
                  >
                    <Grid size ={{xs:10}}>
                      <TextField
                        fullWidth
                        placeholder={`Answer ${aIndex + 1}`}
                        value={a.answerName}
                        onChange={(e) =>
                          handleAnswerChange(qIndex, aIndex, e.target.value)
                        }
                      />
                    </Grid>
                    <Grid size ={{xs:1}}>
                      <FormControlLabel
                        control={
                          <Radio
                            checked={a.isCorrect}
                            onChange={() => handleCorrectAnswer(qIndex, aIndex)}
                          />
                        }
                        label=""
                      />
                    </Grid>
                    <Grid size ={{xs:1}}>
                      <IconButton
                        color="error"
                        onClick={() => removeAnswer(qIndex, aIndex)}
                      >
                        <Remove />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}

                <Box sx={{ textAlign: "right" }}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<Remove />}
                    onClick={() => removeQuestion(qIndex)}
                  >
                    Remove Question
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}

          <Box sx={{ textAlign: "right", mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Add />}
              size="small"
              onClick={addQuestion}
            >
              Add Question
            </Button>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit}
              disabled={creating}
              startIcon={
                creating ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              {creating ? "Creating..." : "Create"}
            </Button>
            <Button
              variant="contained"
              sx={{ background: "rgba(81, 35, 132, 1)" }}
              startIcon={<ArrowBack />}
              onClick={() => navigate("/Question/List")}
            >
              Back to List
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default withAdminAuth(CreateQuestion);
